//-- Copyright (c) 2021 TMT Insights Group, LLC
import React, { createRef, useRef, useContext, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { Modal } from '@/components/_common';
import { StatusCard } from '@/__configs/config_types';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  ListItemText,
  MenuItem,
  Palette,
  Select,
  TextField
} from '@mui/material';
import PolarisIcon from '@/components/_common/PolarisIcon';
import { deepCopy } from '@/_utils/utils';
import { selectJobStages } from '@/redux/selectors/constantsSelectors';
import { selectGraphsEditable } from '@/redux/selectors/dashboardSelectors';
import { setGraphsEditable } from '@/redux/slices/dashboardSlice';
import { ConfigContext } from '@/contextAPI/configContext';
import ResetButton from '@/components/_common/IconButtons/ResetButton';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { nanoid } from 'nanoid';
import { closestCenter, DndContext, DragEndEvent, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import SortableItem from '@/components/_common/SortableItem';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { CHART_STATUS_LABELS } from '../chartUtils';

export interface ConfigureStatusCardsModalProps {
  isOpen: boolean;
  closeWindow: () => void;
  data: StatusCard[];
}

type AccordionStatusCard = Omit<StatusCard, 'filter'> & {
  id: string;
  filter: string[];
  isAccordionOpen: boolean;
};

const ConfigureStatusCardsModal = ({ isOpen, closeWindow, data }: ConfigureStatusCardsModalProps) => {
  const [cards, setCards] = useState<AccordionStatusCard[]>(
    data.map((data) => ({
      ...data,
      id: nanoid(),
      filter: data.filter ? data.filter : [],
      isAccordionOpen: false
    }))
  );
  const graphsEditable = useAppSelector(selectGraphsEditable);
  const defaultStatusCards = useContext(ConfigContext)?.dashboardConfig?.statusCards || CHART_STATUS_LABELS;
  const dispatch = useAppDispatch();
  const cardRefs = useRef<any>(cards.map(() => createRef()));
  const validate = {
    name: (name: string) => name.length >= 1 && name.length <= 20,
    filter: (filter: string[]) => filter.length >= 1
  };
  const hasChanged = useRef(false);

  const jobStageOptions = useAppSelector(selectJobStages);
  const colorOptions = [
    'primary',
    'secondary',
    'tertiary',
    'success',
    'warning',
    'pending',
    'error',
    'default',
    'teal',
    'lavender',
    'info',
    '#952a9f',
    '#dd2277',
    '#71a400'
  ];
  const iconOptions = [
    'clock',
    'timelapse',
    'clock_progress',
    'clock_future',
    'timer',
    'version_based',
    'component_based',
    'draft',
    'search',
    'skip',
    'download',
    'external_link',
    'flight',
    'refresh',
    'priority',
    'robot',
    'settings_filled',
    'user',
    'job',
    'movie',
    'error_circle',
    'check'
  ];

  useEffect(() => {
    setCards(
      data.map((data) => ({
        ...data,
        id: nanoid(),
        filter: data.filter ? data.filter : [],
        isAccordionOpen: false
      }))
    );
    cardRefs.current = data.map(() => createRef());
  }, [data]);

  const handleAccordionToggle = (index: number) => {
    const newCards = deepCopy(cards);
    newCards[index].isAccordionOpen = !cards[index].isAccordionOpen;
    setCards(newCards);
    // wait until the refs update with the new card
    setTimeout(() => {
      const highlightedCard = cardRefs.current[index];
      highlightedCard.current.scrollIntoView({ behavior: 'smooth' });
      // wait until the scroll is finished, otherwise it'll stop the scroll
      setTimeout(() => {
        highlightedCard.current.querySelector('input')?.focus();
      }, 500);
    }, 200);
  };

  const handleFieldUpdate = (index: number, field: string, value: any) => {
    const newCards = deepCopy(cards);
    newCards[index][field] = value;
    setCards(newCards);
    hasChanged.current = true;
  };

  const handleClose = () => {
    const newCards: AccordionStatusCard[] = graphsEditable.statusCards.map((card) => ({
      ...card,
      id: nanoid()
    })) as AccordionStatusCard[];
    setCards(newCards);
    closeWindow();
  };

  const handleReset = () => {
    const newCards: AccordionStatusCard[] = defaultStatusCards.map((card: AccordionStatusCard) => ({
      ...card,
      id: nanoid()
    }));
    setCards(newCards);
  };

  const handleApply = () => {
    dispatch(
      setGraphsEditable({
        ...graphsEditable,
        statusCards: cards.map((card) => ({
          ...card,
          name: card.name.trimEnd()
        })),
        orderBuildType: hasChanged.current ? 'custom' : 'default'
      })
    );
    closeWindow();
  };

  const addDuplicateSuffix = (str: string) => {
    // can't have cards with duplicate statuses - add '_<count>' to the end of the status
    const match = str.match(/_(\d+)$/);
    if (match) {
      const number = parseInt(match[1]);
      return str.replace(/_(\d+)$/, `_${number + 1}`);
    } else {
      return str + '_2';
    }
  };

  const handleAddNewCard = () => {
    const newCards = deepCopy(cards);
    newCards.push({
      id: nanoid(),
      name: '',
      color: 'default',
      filter: [],
      icon: 'clock',
      isAccordionOpen: true,
      status: `new_status_${new Date().getTime().toString()}`
    });
    setCards(newCards);
    cardRefs.current.push(createRef());
    // wait until the refs update with the new card
    setTimeout(() => {
      const newlyAddedCard = cardRefs.current[newCards.length - 1];
      newlyAddedCard.current.scrollIntoView({ behavior: 'smooth' });
      // wait until the scroll is finished, otherwise it'll stop the scroll
      setTimeout(() => {
        newlyAddedCard.current.querySelector('input')?.focus();
      }, 500);
    }, 200);
  };

  const handleDeleteCard = (index: number) => {
    const newCards = deepCopy(cards);
    newCards.splice(index, 1);
    setCards(newCards);
  };

  const handleDuplicateCard = (index: number) => {
    const newCards = deepCopy(cards);

    newCards.splice(index, 0, {
      ...newCards[index],
      id: nanoid(),
      name: newCards[index].name + ' (Copy)',
      status: addDuplicateSuffix(newCards[index].status)
    });

    newCards[index + 1].isAccordionOpen = false;
    setCards(newCards);
    // wait until the refs update with the new card
    setTimeout(() => {
      const copiedCard = cardRefs.current[index];
      copiedCard.current.scrollIntoView({ behavior: 'smooth' });
      // wait until the scroll is finished, otherwise it'll stop the scroll
      setTimeout(() => {
        copiedCard.current.querySelector('input')?.focus();
      }, 500);
    }, 200);
  };

  const splitIntoTwo = (base: string[]) => {
    const halfIndex = Math.ceil(base.length / 2);
    return [base.slice(0, halfIndex), base.slice(halfIndex)];
  };

  const areCardsValid = () => {
    return (
      cards.length >= 4 &&
      cards.length <= 6 &&
      cards.every((card) => {
        return validate.name(card.name) && validate.filter(card.filter);
      })
    );
  };

  const disabledSaveMessage = () => {
    const valid = areCardsValid();

    if (cards.length > 6) {
      return 'Maximum of 6 status cards allowed.';
    } else if (cards.length < 4) {
      return 'Must have at least 4 status cards.';
    } else if (!valid) {
      return 'Please ensure all fields are valid.';
    }
    return '';
  };

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = cards.findIndex((card) => card.id === active.id);
      const newIndex = cards.findIndex((card) => card.id === over.id);
      const newOrder = arrayMove(cards, oldIndex, newIndex);
      setCards(newOrder);
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 3
      }
    }),
    useSensor(KeyboardSensor)
  );
  return (
    <Modal title="Configure Status Cards" open={isOpen} onClose={handleClose} icon={<PolarisIcon icon="settings" />} scrollDisabled>
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflowY: 'hidden' }}>
        <ScrollableDiv>
          <DndContext modifiers={[restrictToVerticalAxis]} sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <LineItemWrapper>
              <SortableContext items={cards.map((card) => card.id)} strategy={verticalListSortingStrategy}>
                {cards.map((card: AccordionStatusCard, index: number) => (
                  <SortableItem id={card.id} key={card.id}>
                    <StyledAccordion ref={cardRefs.current[index]} expanded={!!card.isAccordionOpen}>
                      <StyledAccordionSummary style={{ cursor: 'pointer' }} onClick={() => handleAccordionToggle(index)}>
                        <Grid container alignItems="center" justifyContent="space-between" sx={{ mt: 1.5, mb: 1.5 }}>
                          <Grid item xs={1} sx={{ cursor: 'grab', pl: 1 }}>
                            <GripIcon icon="grip" height={30} width={30} />
                          </Grid>
                          <Grid item xs={9} sx={{ display: 'flex', flexDirection: 'column', pl: 1 }}>
                            <Name color={card.color}>{card.name.length > 0 ? card.name : '-'}</Name>
                            <Filters>
                              {(card.filter ?? []).length > 0 ? (card.filter as string[]).map((item) => jobStageOptions[item]).join(' • ') : '-'}
                            </Filters>
                          </Grid>
                          <Grid
                            item
                            xs={2}
                            sx={{
                              display: 'flex',
                              flexDirection: 'row',
                              alignItems: 'center',
                              justifyContent: 'flex-end',
                              pr: 2,
                              columnGap: 1.5
                            }}
                          >
                            <IconWrapper color={card.color}>
                              <PolarisIcon icon={card.icon} height={24} />
                            </IconWrapper>
                            <EditIcon icon={cards[index].isAccordionOpen ? 'up' : 'down'} height={20} style={{ cursor: 'pointer' }} />
                          </Grid>
                        </Grid>
                      </StyledAccordionSummary>
                      <AccordionDetails style={{ cursor: 'auto' }}>
                        <Divider sx={{ mx: 2 }} />
                        <Grid container rowSpacing={2} columnSpacing={2} sx={{ px: 2, py: 3 }}>
                          <Grid item xs={4}>
                            <NameInput
                              data-prevent-dnd-keydown
                              error={!validate.name(card.name)}
                              helperText={!validate.name(card.name) ? 'Must be between 1-20 characters.' : ' '}
                              variant={'filled'}
                              label={'Card Name'}
                              value={card.name}
                              onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                                if (e.target.value[0] === ' ' || e.target.value.length > 20) return;
                                handleFieldUpdate(index, 'name', e.target.value);
                              }}
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                          <Grid item xs={8}>
                            <JobTypeSelection>
                              <InputLabel shrink error={!validate.filter(card.filter)} id="job-stage-id">
                                Job Stages
                              </InputLabel>
                              <Select
                                labelId="job-stage-id"
                                multiple
                                displayEmpty
                                variant={'filled'}
                                value={card.filter}
                                onChange={(e) => handleFieldUpdate(index, 'filter', e.target.value)}
                                renderValue={(selected) => (selected as string[]).map((status) => jobStageOptions[status]).join(' • ')}
                                MenuProps={{ PaperProps: { style: { maxHeight: '40%', minHeight: 300, width: 200 } } }}
                              >
                                {Object.keys(jobStageOptions).map((option: string) => (
                                  <MenuItem key={option} value={option}>
                                    <Checkbox checked={(card.filter || []).includes(option)} />
                                    <ListItemText primary={jobStageOptions[option]} />
                                  </MenuItem>
                                ))}
                              </Select>
                              <FormHelperText error>
                                {!validate.filter(card.filter) ? 'Must have at least 1 job stage selected.' : ' '}
                              </FormHelperText>
                            </JobTypeSelection>
                          </Grid>
                          <Grid item xs={5}>
                            <BorderWrapper>
                              <span>Color</span>
                              <DoubleRowWrapper>
                                {splitIntoTwo(colorOptions).map((set, setIndex) => (
                                  <Set key={`color-set-${setIndex}`}>
                                    {set.map((option) => (
                                      <Color
                                        key={option}
                                        className={card.color === option ? 'selected' : ''}
                                        id={option}
                                        color={option}
                                        selected={+(card.color === option)}
                                        onClick={() => handleFieldUpdate(index, 'color', option)}
                                      />
                                    ))}
                                  </Set>
                                ))}
                              </DoubleRowWrapper>
                            </BorderWrapper>
                          </Grid>
                          <Grid item xs={7}>
                            <BorderWrapper>
                              <span>Icon</span>
                              <DoubleRowWrapper>
                                {splitIntoTwo(iconOptions).map((set, setIndex) => (
                                  <Set key={`icon-set-${setIndex}`}>
                                    {set.map((option) => (
                                      <PolarisIconOption
                                        key={option}
                                        className={card.icon === option ? 'selected' : ''}
                                        selected={+(card.icon === option)}
                                        icon={option}
                                        onClick={() => handleFieldUpdate(index, 'icon', option)}
                                      />
                                    ))}
                                  </Set>
                                ))}
                              </DoubleRowWrapper>
                            </BorderWrapper>
                          </Grid>
                          <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', columnGap: 2 }}>
                            <Button
                              variant="outlined"
                              color="default"
                              data-prevent-dnd-keydown
                              onClick={() => handleDuplicateCard(index)}
                              startIcon={<PolarisIcon icon="duplicate" height={15} />}
                            >
                              Duplicate
                            </Button>
                            <Button
                              variant="outlined"
                              color="error"
                              data-prevent-dnd-keydown
                              onClick={() => handleDeleteCard(index)}
                              startIcon={<PolarisIcon icon="delete" height={15} />}
                            >
                              Delete
                            </Button>
                          </Grid>
                        </Grid>
                      </AccordionDetails>
                    </StyledAccordion>
                  </SortableItem>
                ))}
              </SortableContext>
            </LineItemWrapper>
          </DndContext>
          <div style={{ marginTop: 10 }}>
            <Button variant="text" color="primary" onClick={handleAddNewCard} startIcon={<PolarisIcon icon="add" height={15} />}>
              Add New Card
            </Button>
          </div>
        </ScrollableDiv>

        <DisabledSaveMessage>{disabledSaveMessage()}</DisabledSaveMessage>
        <ActionButtons>
          <div>
            <ResetButton onClick={handleReset} />
          </div>
          <div>
            <Button variant="text" onClick={handleClose} sx={{ mr: 1.5 }} color="default">
              Cancel
            </Button>
            <Button variant="contained" onClick={handleApply} disabled={!areCardsValid()}>
              Apply
            </Button>
          </div>
        </ActionButtons>
      </div>
    </Modal>
  );
};

export default ConfigureStatusCardsModal;

const NameInput = styled(TextField)`
  width: 100%;
`;

const Name = styled.span<{ color: string }>`
  font-size: 1.3rem;
  font-weight: ${({ theme }) => theme.typography.fontWeightMedium};
  line-height: 2rem;
  color: ${({ color, theme }) => (color.charAt(0) === '#' ? color : theme.palette[color as keyof Palette].main)};
`;

const Filters = styled.span`
  opacity: 0.5;
  font-size: 1.1rem;
`;

const StyledAccordion = styled(Accordion)`
  background: ${({ theme }) => theme.palette.background.default};
`;

const StyledAccordionSummary = styled(AccordionSummary)`
  width: 100%;
`;

const GripIcon = styled(PolarisIcon)`
  min-width: 30px;
  opacity: 0.3;
  margin: ${({ theme }) => theme.spacing('auto', 1)};
`;

const LineItemWrapper = styled.div`
  height: fit-content;
  display: flex;
  flex-direction: column;
  width: 100%;
  row-gap: ${({ theme }) => theme.spacing(1)};
`;

const EditIcon = styled(PolarisIcon)`
  color: ${({ theme }) => theme.palette.primary.main};
`;

const ActionButtons = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-top: ${({ theme }) => theme.spacing(1)};
`;

const IconWrapper = styled.span<{ color: string }>`
  display: flex;
  justify-content: center;
  align-items: center;
  background: ${({ color, theme }) => (color.charAt(0) === '#' ? color + '2a' : theme.palette[color as keyof Palette].main + '2a')};
  color: ${({ color, theme }) => (color.charAt(0) === '#' ? color : theme.palette[color as keyof Palette].main)};
  padding: ${({ theme }) => theme.spacing(0.75)};
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
`;

const JobTypeSelection = styled(FormControl)`
  width: 100%;
  .MuiFormLabel-root {
    top: 15px;
    left: -0px;
  }

  .MuiMenu-paper {
    max-height: 50px; // Set your desired max height here
  }
`;

const BorderWrapper = styled.div`
  border: ${({ theme }) => `1px solid ${theme.palette.divider}`};
  padding: ${({ theme }) => theme.spacing(3.5, 2, 1)};
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
  position: relative;

  span {
    position: absolute;
    top: 11px;
    left: 15px;
    font-size: 0.8rem;
    opacity: 0.8;
    line-height: 0.5rem;
  }
`;

const ScrollableDiv = styled.div`
  max-height: calc(100vh - 220px);
  overflow-y: auto;
`;

const Color = styled.div<{ selected: number; color: string }>`
  width: 20px;
  height: 20px;
  margin: 2.5px 0px;
  background-color: ${({ color, theme }) => (color.charAt(0) === '#' ? color : theme.palette[color as keyof Palette].main)};
  border-radius: 50%;
  position: relative;
  transition: transform 0.2s;
  cursor: pointer;

  ${({ selected, theme }) =>
    selected &&
    `
  &::after {
    content: '';
    position: absolute;
    border: 2px solid ${theme.palette.primary.main};
    width: 100%;
    height: 30px;
    width: 30px;
    border-radius: 50%;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  `}
`;

const PolarisIconOption = styled(PolarisIcon)<{ selected: number }>`
  color: ${({ theme, selected }) => (selected ? theme.palette.primary.main : theme.palette.text.primary)};
  opacity: ${({ selected }) => (selected ? 1 : 0.3)};
  transition: all 0.2s;
  cursor: pointer;
  height: 25px;
  width: 25px;

  &:hover {
    color: ${({ theme }) => theme.palette.text.primary};
    opacity: 0.8;
  }
`;

const Set = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const DoubleRowWrapper = styled.div`
  row-gap: ${({ theme }) => theme.spacing(1)};
  display: flex;
  flex-direction: column;
  margin: ${({ theme }) => theme.spacing(0.5, 0)};
`;

const DisabledSaveMessage = styled.div`
  width: 100%;
  height: 20px;
  text-align: right;
  color: ${({ theme }) => theme.palette.error.main};
  margin-top: ${({ theme }) => theme.spacing(1)};
`;
